import { useMemo, useState } from 'react';
import { useStore } from '../state/store';
import { TopBar } from '../components/TopBar';
import { historicalSeries } from '../engine/projection';
import { fmtMoney, fmtDate, relativeDate } from '../utils/format';
import { HistoryChart } from '../components/HistoryChart';
import type { Snapshot } from '../types';

interface Props {
  onGoTo: (tab: string) => void;
}

export function HistoryPage({ onGoTo }: Props) {
  const { snapshots, accounts, deleteSnapshot, saveSnapshot } = useStore();
  const series = useMemo(() => historicalSeries(snapshots, accounts), [snapshots, accounts]);
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <>
      <TopBar
        title="History"
        subtitle={`${snapshots.length} snapshots`}
        onManageScenarios={() => onGoTo('scenarios')}
      />
      <main className="mx-auto max-w-7xl px-4 py-4 pb-nav lg:px-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:gap-6">
          <div className="space-y-4">
            {series.length >= 2 && (
              <section className="card card-dark p-3">
                <HistoryChart series={series} accounts={accounts} snapshots={snapshots} />
              </section>
            )}
          </div>
          <div className="space-y-4">
            <section className="card card-dark">
              <ul>
            {snapshots.length === 0 && (
              <li className="p-6 text-center text-sm text-ink-500 dark:text-ink-300">
                No snapshots yet. Use Update to add your first one.
              </li>
            )}
            {snapshots.map((s, i) => {
              const prev = snapshots[i + 1];
              const entry = series.find(p => p.date === s.date);
              const prevEntry = prev ? series.find(p => p.date === prev.date) : undefined;
              const delta = entry && prevEntry ? entry.netWorth - prevEntry.netWorth : 0;
              const isExpanded = expanded === s.id;
              return (
                <li
                  key={s.id}
                  className="border-b border-ink-100 last:border-b-0 dark:border-ink-700"
                >
                  <button
                    type="button"
                    onClick={() => setExpanded(x => (x === s.id ? null : s.id))}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left"
                  >
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{fmtDate(s.date)}</div>
                      <div className="text-[11px] text-ink-500 dark:text-ink-300">
                        {relativeDate(s.date)}
                        {s.notes && <> · {s.notes}</>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm font-semibold tabular-nums">
                        {fmtMoney(entry?.netWorth ?? 0)}
                      </div>
                      {delta !== 0 && (
                        <div
                          className={`font-mono text-[11px] tabular-nums ${
                            delta > 0 ? 'text-pos-700 dark:text-pos-500' : 'text-neg-700 dark:text-neg-500'
                          }`}
                        >
                          {delta > 0 ? '+' : '−'}
                          {fmtMoney(Math.abs(delta))}
                        </div>
                      )}
                    </div>
                  </button>
                  {isExpanded && (
                    <SnapshotDetails
                      snap={s}
                      onDelete={async () => {
                        if (confirm('Delete this snapshot?')) await deleteSnapshot(s.id);
                      }}
                      onUpdateNotes={async notes => {
                        await saveSnapshot({ ...s, notes });
                      }}
                    />
                  )}
                </li>
              );
            })}
          </ul>
            </section>
          </div>
        </div>
      </main>
    </>
  );
}

function SnapshotDetails({
  snap,
  onDelete,
  onUpdateNotes,
}: {
  snap: Snapshot;
  onDelete: () => void;
  onUpdateNotes: (n: string) => void;
}) {
  const { accounts } = useStore();
  const [notes, setNotes] = useState(snap.notes ?? '');
  const entries = Object.entries(snap.balances)
    .map(([id, v]) => ({ account: accounts.find(a => a.id === id), v }))
    .filter(e => e.account);

  return (
    <div className="space-y-3 bg-ink-50 px-4 py-3 dark:bg-ink-800/50">
      <ul className="space-y-1">
        {entries.map(e => (
          <li key={e.account!.id} className="flex items-baseline justify-between text-sm">
            <span className="text-ink-700 dark:text-ink-100">{e.account!.name}</span>
            <span className="font-mono tabular-nums">{fmtMoney(e.v)}</span>
          </li>
        ))}
      </ul>
      <textarea
        className="input h-16 text-sm"
        placeholder="Notes"
        value={notes}
        onChange={e => setNotes(e.target.value)}
        onBlur={() => notes !== (snap.notes ?? '') && onUpdateNotes(notes)}
      />
      <button className="btn btn-danger w-full text-sm" onClick={onDelete}>
        Delete snapshot
      </button>
    </div>
  );
}
